<template>
  <div class="min-h-screen">
    <!-- Pantalla de selección de empresa -->
    <div v-if="!selectedEmpresa" class="flex items-center justify-center min-h-screen p-6">
      <div class="max-w-4xl w-full mx-auto">
        <!-- Header -->
        <div class="text-center mb-12">
          <div class="inline-flex items-center justify-center w-41 h-41 rounded-2xl mb-6 overflow-hidden">
            <img :src="LogoNuevoEfintel" alt="Efintel" class="w-41 h-41 object-contain" @error="e => e.target.src = 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'white\'%3E%3Cpath d=\'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5\'/%3E%3C/svg%3E'" />
          </div>
          <!-- <h1 class="text-3xl font-bold text-gray-900 mb-2">Bienvenido a efibox</h1> -->
          <p class="text-gray-500 text-lg">Selecciona una empresa para comenzar a monitorear tus dispositivos</p>
        </div>

        <!-- Grid de empresas -->
        <div v-if="empresasDisponibles.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div v-for="empresa in empresasDisponibles" :key="empresa.id" @click="handleEmpresaSelected(empresa)" class="group bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden">
            <div class="p-6">
              <div class="flex items-start justify-between mb-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span><img v-if="empresa.media" :src="empresa.media" class="w-full h-full object-cover" alt="Logo"></span>
                </div>
              </div>

              <h3 class="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                {{ empresa.nombre_empresa || empresa.nombre }}
              </h3>

              <div class="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <div class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  </svg>
                  <span>{{ obtenerEstadisticasEmpresa(empresa.id).locales }} locales</span>
                </div>
                <div class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
                  </svg>
                  <span>{{ obtenerEstadisticasEmpresa(empresa.id).dispositivos }} dispositivos</span>
                </div>
              </div>

              <div class="flex items-center justify-between pt-3 border-t border-gray-100">
                <span class="text-xs text-gray-400">{{ empresa.ciudad_empresa }}</span>
                <span class="text-xs text-blue-600 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                  Ver locales
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-12">
          <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
            </svg>
          </div>
          <p class="text-gray-500">No hay empresas disponibles</p>
        </div>
      </div>
    </div>

    <!-- Vista de locales -->
    <LocalesView v-else-if="selectedEmpresa && !selectedLocal" :empresa="selectedEmpresa" :loading="loadingLocales" @local-selected="handleLocalSelected" @back-to-empresas="handleBackToEmpresas" />

    <!-- Vista principal del local -->
    <div v-else class="relative">
      <!-- Breadcrumb mejorado -->
      <div class="bg-white border-b border-gray-200 px-2">
        <nav class="flex items-center space-x-2 text-sm p-2">
          <button @click="handleBackToEmpresas" class="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
            </svg>
            <span>Empresas</span>
          </button>

          <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>

          <button @click="handleBackToLocales" class="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
            <span>{{ selectedEmpresa.nombre_empresa || selectedEmpresa.nombre }}</span>
          </button>

          <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>

          <span class="text-gray-900 font-medium">{{ selectedLocal.nombre || selectedLocal.nombre_local }}</span>
        </nav>
      </div>

      <!-- Dashboard principal -->
      <mainDashboard :local="selectedLocal" :empresa="selectedEmpresa" />
    </div>

    <!-- Loader mejorado -->
    <div v-if="isLoading" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div class="bg-white rounded-xl shadow-xl p-6 flex items-center gap-4">
        <div class="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <div>
          <p class="text-gray-900 font-medium">{{ loadingMessage }}</p>
          <p class="text-sm text-gray-500">Por favor espera un momento</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainDashboard from '@/components/dashboard/mainDashboard.vue'
import LocalesView from '@/components/locales/LocalesView.vue'

import LogoNuevoEfintel from '/src/assets/logoNuevoEfintelSinFondo.png'


export default {
  name: 'AppContent',

  components: {
    mainDashboard,
    LocalesView
  },

  props: {
    selectedEmpresa: {
      type: Object,
      default: null
    },
    selectedLocal: {
      type: Object,
      default: null
    },
    empresasDisponibles: {
      type: Array,
      default: () => []
    }
  },

  emits: [
    'local-selected',
    'back-to-empresas',
    'back-to-locales',
    'empresa-selected'
  ],

  data() {
    return {
      isLoading: false,
      loadingLocales: false,
      loadingMessage: 'Cargando...',
      estadisticasEmpresas: new Map(), // Map<empresaId, {locales: number, dispositivos: number}>
      LogoNuevoEfintel
    }
  },

  computed: {
    /**
     * Determina qué vista mostrar según el estado
     */
    currentView() {
      if (!this.selectedEmpresa) return 'empresas';
      if (this.selectedEmpresa && !this.selectedLocal) return 'locales';
      return 'dashboard';
    }
  },

  methods: {
    // ---------------------------------------------------
    // Navegación y Selección
    // ---------------------------------------------------
    handleEmpresaSelected(empresa) {
      this.$emit('empresa-selected', empresa);
    },
    // Maneja la selección de un local y emite evento
    handleLocalSelected(local) {
      this.$emit('local-selected', local);
    },

    // Vuelve a la vista de empresas emitiendo evento
    handleBackToEmpresas() {
      this.$emit('back-to-empresas');
    },

    // Vuelve a la vista de locales emitiendo evento
    handleBackToLocales() {
      this.$emit('back-to-locales');
    },

    // ---------------------------------------------------
    // Gestión de Datos y Estadísticas
    // ---------------------------------------------------
    // Carga estadísticas de locales y dispositivos por empresa
    async cargarEstadisticasEmpresas() {
      if (this.empresasDisponibles.length === 0) return;

      try {
        const LocalesService = (await import('@/services/locales/locales.js')).default;
        const DispositivosService = (await import('@/services/dispositivos/dispositivos.js')).default;

        const [todosLocales, todosDispositivos] = await Promise.all([
          LocalesService.obtenerTodosLosLocales().catch(() => []),
          DispositivosService.obtenerTodosLosDispositivos().catch(() => [])
        ]);

        this.empresasDisponibles.forEach(empresa => {
          const localesEmpresa = todosLocales.filter(l => l.id_empresa === empresa.id);
          const cantidadLocales = localesEmpresa.length;

          const idsLocalesEmpresa = localesEmpresa.map(l => l.id);
          const dispositivosEmpresa = todosDispositivos.filter(d =>
            idsLocalesEmpresa.includes(d.local_id)
          );
          const cantidadDispositivos = dispositivosEmpresa.length;

          this.estadisticasEmpresas.set(empresa.id, {
            locales: cantidadLocales,
            dispositivos: cantidadDispositivos
          });
        });

      } catch (error) {
        console.error('Error cargando estadísticas:', error);
      }
    },
    // Obtiene estadísticas de una empresa específica del mapa
    obtenerEstadisticasEmpresa(empresaId) {
      return this.estadisticasEmpresas.get(empresaId) || { locales: 0, dispositivos: 0 };
    },

    // ---------------------------------------------------
    // Gestión de Estados de UI
    // ---------------------------------------------------
    // Muestra el loading global con mensaje personalizado
    showLoading(message = 'Cargando...') {
      this.isLoading = true;
      this.loadingMessage = message;
    },
    // Oculta el loading global
    hideLoading() {
      this.isLoading = false;
    }
  },

  watch: {
    /**
     * Watch para cargar estadísticas cuando cambien las empresas
     */
    empresasDisponibles: {
      async handler(nuevasEmpresas) {
        if (nuevasEmpresas && nuevasEmpresas.length > 0) {
          await this.cargarEstadisticasEmpresas();
        }
      },
      immediate: true
    }
  },

  mounted() {
    // Cargar estadísticas si ya hay empresas disponibles
    if (this.empresasDisponibles.length > 0) {
      this.cargarEstadisticasEmpresas();
    }
  }
}
</script>

<style scoped></style>